import Button from "@material-ui/core/Button";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { connect, useDispatch } from "react-redux";
import { clearSubmitErrors, Field, getFormValues, reduxForm, submit } from "redux-form";
import PaymentService from "../../../../services/payments";
import { authorizationActionCreators } from "../../../../store/actions/authorization";
import { FormPaymentTypeField, FormTextField } from "../../../../components/fields";
import ProgressButton from "../../../../components/ProgressButton";
import { push } from "../../../../store/actions/snackbar";
import PaymentAuthorizationForm from "../../../PaymentAuthorization";
import { FormFooter } from "../FormFooter";
import Stripe from "./Stripe/Stripe";
import styles from "./style.module.css";
import { ReactComponent as AddIcon } from './add-icon.svg';
import TextField from "@material-ui/core/TextField/TextField";

const queryConfig = {
  retry: false,
  refetchOnWindowFocus: false,
  queryFnParamsFilter: args => args.slice(1),
};

const Payments = React.memo((props) => {
  const dispatch = useDispatch();
  const [ openStripe, setOpenStripe ] = useState(false);
  const [ tokenLoading, setTokenLoading ] = useState(false);
  const [ modalLoading, setModalLoading ] = useState(false);
  const { handleSubmit, submitting, openModal, showPaymentAuthorizedModal, isFirstName, isLastName, isEmail, formData } = props;
  const { data: paymentTypes = [], refetch: refetchPaymentTypes } = useQuery("paymentTypes", PaymentService.getCard, queryConfig);

  useEffect(() => {
    if (modalLoading) {
      setModalLoading(false);
      setOpenStripe(false);
    }
  }, [ paymentTypes ]);

  const deletePaymentMethod = (token) => {
    const promise = PaymentService.deleteCard(token);
    promise.then(() => refetchPaymentTypes());
  };

  const getVerifiedUserInfo = () => {
    return (!!isFirstName && !!isLastName && !!isEmail);
  };

  const getPaymentToken = () => {
    setTokenLoading(true);
    const serverToken = PaymentService.getToken();
    serverToken.then(() => {
      setTokenLoading(false);
      setOpenStripe(true);
    });
  };

  const stripeTokenHandler = async (paymentMethod) => {
    try {
      await PaymentService.putCard({
        token: paymentMethod.id,
        name: "•• " + paymentMethod.card.last4,
        status: "VERIFIED",
        paymentType: "CARD",
        bankCardType: paymentMethod.card.brand?.toUpperCase?.(),
      });
    } catch (e) {
      let errorMessage;
      if (e?.errorCode === 1501) {
        errorMessage = "Your card has insufficient funds";
      } else if (e?.errorMessage) {
        errorMessage = e?.errorMessage;
      } else {
        errorMessage = JSON.stringify(e);
      }
      dispatch(push("Card error", errorMessage, "error"));
    } finally {
      await refetchPaymentTypes();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className={styles.main}>
          {
            getVerifiedUserInfo() ?
              <>
                <TextField
                  value={isFirstName + ' ' + isLastName}
                  label="Full name"
                  variant="outlined"
                  disabled
                />
                <TextField
                  value={isEmail}
                  label="Email"
                  variant="outlined"
                  disabled
                />
              </> :
              <Button type='button'
                      variant='contained'
                      disabled={submitting}
                      onClick={openModal}
              >
                Please complete profile
              </Button>
          }
          {showPaymentAuthorizedModal && <PaymentAuthorizationForm />}
          <div className={styles.paymentContainer}>
            <Field name="paymentType"
                   component={FormPaymentTypeField}
                   options={paymentTypes}
                   delete={deletePaymentMethod}
            />
          </div>
          {getVerifiedUserInfo() &&
          <div className={styles.stripeSection}>
            <Stripe
              openStripeModal={openStripe}
              closeStripeModal={() => setOpenStripe(false)}
              stripeTokenHandler={stripeTokenHandler}
              modalLoading={modalLoading}
              setModalLoading={setModalLoading}
            />
            <Button
              color="inherit"
              onClick={getPaymentToken}
              className={styles.addPaymentButton}
              disabled={tokenLoading}
            >
              <AddIcon />
              Add payment method
            </Button>
          </div>}
          <FormFooter>
            <ProgressButton
              type='submit'
              onClick={() => dispatch(submit("reservation"))}
              color="primary"
              variant='contained'
              isFetching={submitting}
              disabled={submitting || !formData?.paymentType?.token}
              className={styles.button}
            >
              Confirm
            </ProgressButton>
          </FormFooter>
        </div>
      </form>
    </>
  );
});

const InitializeFromStateForm = reduxForm({
  form: "reservation",
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  onChange: (values, dispatch, props) => {
    if (props.error) dispatch(clearSubmitErrors("reservation"));
  },
})(Payments);

const mapStateToProps = (state) => {
  return {
    isFirstName: state.authorization?.client?.firstName,
    isLastName: state.authorization?.client?.lastName,
    isEmail: state.authorization?.client?.email,
    showPaymentAuthorizedModal: state.authorization.showPaymentAuthorizedModal,
    formData: getFormValues("reservation")(state),
  };
};


export default connect(mapStateToProps, (dispatch) => {
  return ({
    openModal: () => {
      dispatch(authorizationActionCreators.handlePaymentAuthorizedModal());
    },
  });
})(InitializeFromStateForm);
